import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../../../types/commonTypes'
import invoicingApi from '../../../../../middleware/api/ProjectManagement/Projects/ProjectView/Invoices/invoicingDiscussionApi'
import {
  InvoiceDetailsForDiscussion,
  InvoiceNewsFeed,
  InvoicingDiscussionProps,
  InvoicingSliceState,
  PostInvoiceProps,
} from '../../../../../types/ProjectManagement/Project/ProjectView/Invoices/invoiceDiscussionListTypes'
import { AppDispatch, RootState } from '../../../../../stateStore'
import { ApiLoadingState } from '../../../../../middleware/api/apiList'

const invoiceDetailsForDiscussion = createAsyncThunk(
  'invoiceDetails/invoiceDetailsForDiscussion',
  async (id: number, thunkApi) => {
    try {
      return await invoicingApi.invoiceDetailsForDiscussion(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getInvoicingNewsFeed = createAsyncThunk(
  'invoiceDetails/getInvoicingNewsFeed',
  async (props: InvoicingDiscussionProps, thunkApi) => {
    try {
      return await invoicingApi.invoiceNewsFeed(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const postProjectInvoice = createAsyncThunk<
  number | string,
  PostInvoiceProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'postInvoiceProps/postProjectInvoice',
  async (postInvoiceProps: PostInvoiceProps, thunkApi) => {
    try {
      return await invoicingApi.postProjectInvoice(postInvoiceProps)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialInvoicingListState: InvoicingSliceState = {
  invoiceDetailsForDiscussion: {} as InvoiceDetailsForDiscussion,
  isLoading: ApiLoadingState.idle,
  invoiceNewsFeed: [],
}

const invoicingDiscussionSlice = createSlice({
  name: 'invoiceDetails',
  initialState: initialInvoicingListState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(invoiceDetailsForDiscussion.fulfilled, (state, action) => {
        state.invoiceDetailsForDiscussion = action.payload
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(invoiceDetailsForDiscussion.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getInvoicingNewsFeed.fulfilled, (state, action) => {
        state.invoiceNewsFeed = action.payload
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getInvoicingNewsFeed.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(postProjectInvoice.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(postProjectInvoice.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
  },
})

const addTaxListThunk = {
  invoiceDetailsForDiscussion,
  getInvoicingNewsFeed,
  postProjectInvoice,
}

const isLoading = (state: RootState): LoadingState =>
  state.invoiceDiscussion.isLoading

const invoiceDetailsForDiscussionResponse = (
  state: RootState,
): InvoiceDetailsForDiscussion =>
  state.invoiceDiscussion.invoiceDetailsForDiscussion

const invoiceNewsFeedList = (state: RootState): InvoiceNewsFeed[] =>
  state.invoiceDiscussion.invoiceNewsFeed

const invoiceDiscussionSelectors = {
  isLoading,
  invoiceDetailsForDiscussionResponse,
  invoiceNewsFeedList,
}

export const invoicingDiscussionService = {
  ...addTaxListThunk,
  actions: invoicingDiscussionSlice.actions,
  selectors: invoiceDiscussionSelectors,
}

export default invoicingDiscussionSlice.reducer
